import { dateUtils, type MappedLocation, type RouteState } from 'shared';

export const cityCode = (code: string) => code.slice(0, 3);

export const locationsChainMatch = (
  arrival?: MappedLocation,
  nextDeparture?: MappedLocation,
) => {
  if (!arrival?.code || !nextDeparture?.code) {
    return false;
  }
  return cityCode(arrival.code) === cityCode(nextDeparture.code);
};

/**
 * Collapses multi-city routes into a single simple-search route.
 * Uses the first two legs for a possible round-trip when the chain matches.
 */
export const buildSimplePartialRouteFromMultiRoutes = (
  routes: RouteState[],
): Partial<{
  dep: MappedLocation;
  arr: MappedLocation;
  dates: string[];
}> => {
  const [firstLeg, secondLeg] = routes;
  if (!firstLeg) {
    return { dates: [dateUtils.getTomorrowDate()] };
  }

  if (!secondLeg || routes.length < 2) {
    let dates: string[];
    if (firstLeg.dates?.length === 2) {
      dates = [...firstLeg.dates];
    } else if (firstLeg.dates?.[0]) {
      dates = [firstLeg.dates[0]];
    } else {
      dates = [dateUtils.getTomorrowDate()];
    }
    return {
      dep: firstLeg.dep,
      arr: firstLeg.arr,
      dates,
    };
  }

  if (locationsChainMatch(firstLeg.arr, secondLeg.dep)) {
    const isRoundTrip = locationsChainMatch(secondLeg.arr, firstLeg.dep);

    if (isRoundTrip) {
      const firstDepartureDate = firstLeg.dates?.[0];
      const secondDepartureDate = secondLeg.dates?.[0];
      let dates: string[];
      if (firstDepartureDate && secondDepartureDate) {
        dates = [firstDepartureDate, secondDepartureDate];
      } else if (firstDepartureDate) {
        dates = [firstDepartureDate];
      } else {
        dates = [dateUtils.getTomorrowDate()];
      }
      return { dep: firstLeg.dep, arr: firstLeg.arr, dates };
    }
  }

  return {
    dep: firstLeg.dep,
    arr: firstLeg.arr,
    dates: firstLeg.dates?.[0]
      ? [firstLeg.dates[0]]
      : [dateUtils.getTomorrowDate()],
  };
};
